import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/* Forward auto-loading plus real server page links. No speculative page fetches.
 * Component owns refs; every response is fenced by filter epoch, grid and URL state.
 * Failed requests stop automatic retries. The button and native page links remain.
 */
export default class PaginatedList extends Component {
  pages = new Map();
  #epoch = 0;
  #loading = false;
  #blocked = false;
  #observer;
  #lifetime;
  #timer;
  #cancelPending;
  #filterWork = new Set();
  #seen = new Set();

  get sectionId() { return this.getAttribute('section-id'); }

  connectedCallback() {
    super.connectedCallback();
    this.#lifetime?.abort();
    this.#lifetime = new AbortController();
    const { signal } = this.#lifetime;
    document.addEventListener(StandardEvents.searchUpdate, this.#onFilter, { signal });
    document.addEventListener(StandardEvents.collectionUpdate, this.#onFilter, { signal });
    document.addEventListener('visibilitychange', this.#onVisibility, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#epoch++;
    this.#lifetime?.abort();
    this.#observer?.disconnect();
    this.#cancelPending?.();
    this.#cancelPending = undefined;
    clearTimeout(this.#timer);
    this.#loading = false;
    this.#filterWork.clear();
    this.pages.clear();
    this.#seen.clear();
  }

  #grid() { return this.querySelector('[ref="grid"]'); }
  #pager() { return this.querySelector('[data-veyro-pagination]'); }
  #cards(grid) { return Array.from(grid?.children || []).filter(node => node.matches('[ref="cards[]"]')); }

  #stateKey(input = window.location.href) {
    const url = new URL(input, window.location.href);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.href;
  }

  #pageURL(raw, expectedPage) {
    const provided = new URL(raw, window.location.href);
    const current = new URL(window.location.href);
    if (provided.origin !== current.origin || provided.pathname !== current.pathname) throw Error('Page route changed');
    const page = Number(provided.searchParams.get('page') || 1);
    if (!Number.isInteger(page) || page < 1 || (expectedPage && page !== expectedPage)) throw Error('Invalid page');
    // Keep the active search, availability, sort and every facet. Only page changes.
    current.searchParams.set('page', String(page));
    current.searchParams.delete('section_id');
    current.hash = '';
    return current;
  }

  #reset() {
    this.#epoch++;
    this.#cancelPending?.();
    this.#cancelPending = undefined;
    clearTimeout(this.#timer);
    this.#loading = false;
    this.#blocked = false;
    this.pages.clear();
    this.#seen.clear();
    this.#patchLinks();
    this.#setBusy(false);
    this.#observeViewMore();
  }

  #patchLinks() {
    for (const link of this.#pager()?.querySelectorAll('.pagination a[href]') || []) {
      try { link.href = this.#pageURL(link.getAttribute('href')).href; } catch { /* Native link remains usable. */ }
    }
  }

  #setBusy(busy) {
    if (busy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    const button = this.#pager()?.querySelector('[data-veyro-more]');
    if (button) {
      button.hidden = false;
      button.disabled = busy || this.#filterWork.size > 0;
      button.textContent = busy ? 'Завантажуємо…' : 'Показати ще товари';
    }
  }

  refreshPagination() { this.#observeViewMore(); }

  #observeViewMore() {
    this.#observer?.disconnect();
    if (!this.isConnected || this.#blocked || this.#filterWork.size || document.visibilityState === 'hidden') return;
    if (this.getAttribute('infinite-scroll') === 'false' || navigator.connection?.saveData) return;
    const pager = this.#pager();
    const sentinel = this.querySelector('[ref="viewMoreNext"]');
    if (!pager?.dataset.nextUrl || !sentinel || typeof IntersectionObserver !== 'function') return;
    this.#observer ??= new IntersectionObserver(entries => {
      const current = this.querySelector('[ref="viewMoreNext"]');
      if (entries.some(entry => entry.target === current && entry.isIntersecting)) this.#loadNext(false);
    }, { rootMargin: '160px 0px' });
    // Re-observe after EVERY append/layout change, even if the sentinel never left
    // the viewport. Dense grids otherwise receive only their first IO callback.
    this.#observer.observe(sentinel);
  }

  #onVisibility = () => {
    if (document.visibilityState === 'hidden') this.#observer?.disconnect();
    else this.#observeViewMore();
  };
  #onPageShow = event => { if (event.persisted && this.isConnected) this.#reset(); };

  #onFilter = event => {
    const owner = event.target?.closest?.('[id^="shopify-section-"]');
    if (owner && owner.id !== `shopify-section-${this.sectionId}`) return;
    this.#epoch++;
    this.#observer?.disconnect();
    this.#loading = false;
    this.#cancelPending?.();
    this.#cancelPending = undefined;
    clearTimeout(this.#timer);
    this.pages.clear();
    const work = event.promise || Symbol('filter');
    this.#filterWork.add(work);
    this.#setBusy(false);
    const finish = () => {
      if (!this.#filterWork.delete(work)) return;
      if (this.isConnected && this.#filterWork.size === 0) this.#reset();
    };
    Promise.resolve(event.promise).then(finish, finish);
  };

  loadNextPage(event) {
    event?.preventDefault?.();
    return this.#loadNext(true);
  }

  async #loadNext(manual) {
    if (this.#loading || this.#filterWork.size || !this.isConnected || this.hidden || (!manual && (this.#blocked || document.visibilityState === 'hidden' || navigator.connection?.saveData))) return;
    const grid = this.#grid();
    const pager = this.#pager();
    if (!grid || !pager?.dataset.nextUrl) return;
    const epoch = this.#epoch;
    const state = this.#stateKey();
    const expected = Number(pager.dataset.page) + 1;
    let url;
    try {
      url = this.#pageURL(pager.dataset.nextUrl, expected);
      if (this.#seen.has(url.href)) return;
    } catch {
      this.#blocked = true;
      this.#observer?.disconnect();
      return;
    }
    this.#loading = true;
    this.#blocked = false;
    this.#setBusy(true);
    const valid = () => this.isConnected && epoch === this.#epoch && grid === this.#grid() && state === this.#stateKey();
    const focusRequested = manual && pager.contains(document.activeElement);
    try {
      const timeout = new Promise((_, reject) => {
        this.#cancelPending = () => reject(Error('Page load cancelled'));
        this.#timer = setTimeout(() => reject(Error('Page load timeout')), 15000);
      });
      const request = Promise.resolve().then(() => {
        if (!valid()) throw Error('Page context changed');
        return sectionRenderer.getSectionHTML(this.sectionId, true, url);
      });
      const html = await Promise.race([request, timeout]);
      if (!valid()) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const owner = Array.from(parsed.querySelectorAll('results-list')).find(node => node.getAttribute('section-id') === this.sectionId);
      const nextGrid = owner?.querySelector('[ref="grid"]');
      const nextPager = owner?.querySelector('[data-veyro-pagination]');
      const incoming = this.#cards(nextGrid);
      if (!nextPager || Number(nextPager.dataset.page) !== expected || !incoming.length || incoming.some(card => Number(card.dataset.page) !== expected)) {
        throw Error('Unexpected page response');
      }
      const existing = new Set(this.#cards(grid).map(card => card.dataset.productId).filter(Boolean));
      const fresh = incoming.filter(card => {
        const id = card.dataset.productId;
        if (!id) return true;
        if (existing.has(id)) return false;
        existing.add(id);
        return true;
      });
      if (!valid()) return;
      grid.append(...fresh);
      pager.replaceWith(nextPager);
      this.#seen.add(url.href);
      super.updatedCallback();
      this.#patchLinks();
      const status = this.#pager()?.querySelector('[data-veyro-page-status]');
      if (status) status.textContent = `Завантажено товарів: ${this.#cards(grid).length}. Сторінка ${expected} з ${nextPager.dataset.pages}.`;
      try { history.replaceState(history.state, '', url); } catch { /* Embedded previews can restrict history. */ }
      if (focusRequested) {
        const first = fresh[0];
        const link = first?.querySelector('a[href]');
        if (link && document.activeElement === document.body) {
          link.focus({ preventScroll: true });
          first.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      }
    } catch {
      if (valid()) {
        this.#blocked = true;
        this.#observer?.disconnect();
        const status = this.#pager()?.querySelector('[data-veyro-page-status]');
        if (status) status.textContent = 'Не вдалося завантажити товари. Натисніть «Показати ще» або оберіть номер сторінки.';
      }
    } finally {
      if (epoch === this.#epoch) {
        clearTimeout(this.#timer);
        this.#cancelPending = undefined;
        this.#loading = false;
        this.#setBusy(false);
        if (valid() && !this.#blocked) this.#observeViewMore();
      }
    }
  }
}
